import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { NumberingFields } from './InvoiceNumberingFields';
import { useToast } from '../hooks/use-toast';
import { FileText, Receipt, Save, Loader2, MapPin } from 'lucide-react';
import MaterialCatalogManager from './MaterialCatalogManager';

const API = `${window.__BACKEND_URL__}/api`;

/**
 * InvoiceTemplateSettings — admin-only company-level invoice template config.
 *
 * Lets the admin set:
 *  - Tax ID (GST/HST/Sales Tax registration number)
 *  - Tax label + rate (e.g. "HST" + 13%)
 *  - Invoice number prefix (e.g. "INV-" → INV-2026-0001)
 *  - Reply-To email (where customer replies on emailed invoices go)
 *  - Payment terms (one-liner shown above the footer)
 *  - Footer text (free-form — payment instructions, e-transfer email, etc.)
 *
 * Logo upload reuses the existing /api/company/upload-logo endpoint — same
 * logo is shown in the dashboard branding bar AND on every invoice PDF.
 */
export default function InvoiceTemplateSettings({ token }) {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    invoice_tax_id: '',
    invoice_tax_label: 'Tax',
    invoice_tax_rate: 0,
    invoice_reply_to: '',
    invoice_payment_terms: 'Net 30',
    invoice_footer_text: '',
    invoice_prefix: 'INV-',
    // Phase 2 (Feb 2026) — currency + markup defaults
    invoice_currency: 'USD',     // 3-letter ISO; blank = auto-detect from country
    material_markup_pct: 0,      // company-wide default; customer/job/line can override
  });
  const [counter, setCounter] = useState(0);
  const [lastUsed, setLastUsed] = useState(null);
  const [logo, setLogo] = useState('');
  // Region-aware tax suggestion driven by the company's registered business
  // address (Settings → Company Information → state/country). The super-user
  // controls that address; if they update it, the suggestion auto-updates.
  const [suggestion, setSuggestion] = useState(null);
  const [businessState, setBusinessState] = useState('');
  const [businessCountry, setBusinessCountry] = useState('');
  const [presets, setPresets] = useState(null);
  const [showPresets, setShowPresets] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoice/settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm({
        invoice_tax_id:        r.data.invoice_tax_id || '',
        invoice_tax_label:     r.data.invoice_tax_label || 'Tax',
        invoice_tax_rate:      Number(r.data.invoice_tax_rate || 0),
        invoice_reply_to:      r.data.invoice_reply_to || '',
        invoice_payment_terms: r.data.invoice_payment_terms || 'Net 30',
        invoice_footer_text:   r.data.invoice_footer_text || '',
        invoice_prefix:        r.data.invoice_prefix || 'INV-',
        invoice_number_format: r.data.invoice_number_format || null,
        invoice_seq_padding:   r.data.invoice_seq_padding || 4,
        invoice_next_number:   r.data.invoice_next_number || ((r.data.invoice_counter || 0) + 1),
        invoice_currency:      r.data.invoice_currency || 'USD',
        material_markup_pct:   Number(r.data.material_markup_pct || 0),
      });
      setCounter(r.data.invoice_counter || 0);
      setLastUsed(r.data.last_used_number || null);
      setLogo(r.data.company_logo || '');
      setSuggestion(r.data.suggested_tax || null);
      setBusinessState(r.data.business_state || '');
      setBusinessCountry(r.data.business_country || '');
    } catch (e) {
      toast({
        title: 'Could not load invoice settings',
        description: e.response?.data?.detail || 'Please retry.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [token, toast]);

  useEffect(() => { load(); }, [load]);

  const applySuggestion = () => {
    if (!suggestion) return;
    setForm((f) => ({ ...f, invoice_tax_label: suggestion.label, invoice_tax_rate: Number(suggestion.rate || 0) }));
    toast({
      title: `Applied: ${suggestion.label} ${Number(suggestion.rate || 0)}%`,
      description: `Based on your business address in ${suggestion.region_name}. Click "Save" to persist.`,
    });
  };

  const openPresetPicker = async () => {
    if (!presets) {
      try {
        const r = await axios.get(`${API}/invoice/tax-presets`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setPresets(r.data);
      } catch (e) {
        toast({
          title: 'Could not load tax presets',
          description: e.response?.data?.detail || 'Please retry.',
          variant: 'destructive',
        });
        return;
      }
    }
    setShowPresets(true);
  };

  const applyPreset = (p) => {
    setForm((f) => ({ ...f, invoice_tax_label: p.label, invoice_tax_rate: Number(p.rate || 0) }));
    setShowPresets(false);
    toast({
      title: `Applied: ${p.label} ${Number(p.rate || 0)}% (${p.region_name})`,
      description: p.notes || 'Click "Save" to persist.',
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      try {
        await axios.put(`${API}/invoice/settings`, form, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        if (e.response?.status === 409 && window.confirm(`${e.response.data.detail}\n\nUse this starting number anyway?`)) {
          await axios.put(`${API}/invoice/settings`, { ...form, force: true }, { headers: { Authorization: `Bearer ${token}` } });
        } else { throw e; }
      }
      toast({ title: 'Invoice settings saved' });
      const r = await axios.get(`${API}/invoice/settings`, { headers: { Authorization: `Bearer ${token}` } });
      setCounter(r.data.invoice_counter || 0);
      setLastUsed(r.data.last_used_number || null);
      setForm((f) => ({ ...f, invoice_next_number: r.data.invoice_next_number }));
    } catch (e) {
      toast({
        title: 'Could not save',
        description: e.response?.data?.detail || 'Please retry.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  // Reuse the existing logo uploader (same endpoint as the dashboard logo).
  const onLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please pick an image file.', variant: 'destructive' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await axios.post(`${API}/company/upload-logo`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogo(r.data?.logo_url || file.name);
      toast({ title: 'Logo updated — it will appear on every new invoice.' });
    } catch (err) {
      toast({
        title: 'Logo upload failed',
        description: err.response?.data?.detail || 'Please retry.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="rounded-xl p-6 flex items-center gap-2" style={{ background: '#0F1923', border: '1px solid #2D3748', color: '#94A3B8' }}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading invoice settings…
      </div>
    );
  }

  const labelClass = 'block text-xs font-medium mb-1';
  const inputClass = 'w-full px-3 py-2 rounded-lg text-sm bg-[#0B1321] border border-[#2D3748] text-white focus:outline-none focus:ring-2 focus:ring-blue-500/40 placeholder:text-[#475569]';
  const labelStyle = { color: '#94A3B8' };

  return (
    <div className="space-y-6" data-testid="invoice-template-settings">
      <div className="rounded-xl p-6" style={{ background: '#0F1923', border: '1px solid #2D3748' }}>
        <div className="flex items-center gap-2 mb-1">
          <Receipt className="w-5 h-5" style={{ color: '#1E88E5' }} />
          <h2 className="text-lg font-semibold text-white">Invoice template</h2>
        </div>
        <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>
          These settings power the HinesTime PDF invoices you can generate from any completed job —
          QuickBooks-independent. Admins fill this in once; every invoice picks it up.
        </p>

        {/* Logo */}
        <div className="mb-6 pb-6" style={{ borderBottom: '1px solid #1A2433' }}>
          <label className={labelClass} style={labelStyle}>Company logo (shown on every invoice)</label>
          <div className="flex items-center gap-4 mt-1">
            <div
              className="w-24 h-16 rounded-lg flex items-center justify-center overflow-hidden flex-shrink-0"
              style={{ background: '#0B1321', border: '1px dashed #2D3748' }}
            >
              {logo ? (
                <img
                  src={logo.startsWith('http') || logo.startsWith('data:') ? logo : `${API}/company/logo/${logo}`}
                  alt="logo"
                  className="max-w-full max-h-full object-contain"
                  data-testid="invoice-logo-preview"
                />
              ) : (
                <span className="text-xs" style={{ color: '#475569' }}>No logo</span>
              )}
            </div>
            <label
              className="px-3 py-2 rounded-lg text-sm cursor-pointer transition-colors"
              style={{ background: '#1A2433', border: '1px solid #2D3748', color: '#E2E8F0' }}
              data-testid="invoice-logo-upload-label"
            >
              Upload logo
              <input type="file" accept="image/*" onChange={onLogoUpload} className="hidden" data-testid="invoice-logo-upload" />
            </label>
          </div>
        </div>

        {/* Tax — region-aware suggestion banner */}
        {suggestion ? (
          <div
            className="mb-3 p-3 rounded-lg flex items-start gap-3"
            style={{
              background: 'rgba(30,136,229,0.08)',
              border: '1px solid rgba(30,136,229,0.30)',
            }}
            data-testid="tax-suggestion-banner"
          >
            <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#1E88E5' }} />
            <div className="flex-1 min-w-0 text-sm">
              <p className="text-white">
                Based on your business address in{' '}
                <strong>{suggestion.region_name}{suggestion.country === 'CA' ? ', Canada' : suggestion.country === 'US' ? ', USA' : ''}</strong>,
                the typical rate is{' '}
                <strong style={{ color: '#1E88E5' }}>
                  {suggestion.label} {Number(suggestion.rate).toFixed(suggestion.rate % 1 === 0 ? 0 : 2)}%
                </strong>.
              </p>
              {suggestion.notes && (
                <p className="text-xs mt-1" style={{ color: '#94A3B8' }}>{suggestion.notes}</p>
              )}
              <div className="flex flex-wrap gap-2 mt-2">
                <button
                  onClick={applySuggestion}
                  className="text-xs px-3 py-1 rounded-md font-medium transition-colors"
                  style={{ background: '#1E88E5', color: '#fff' }}
                  data-testid="apply-tax-suggestion"
                >
                  Apply {suggestion.label} {Number(suggestion.rate).toFixed(suggestion.rate % 1 === 0 ? 0 : 2)}%
                </button>
                <button
                  onClick={openPresetPicker}
                  className="text-xs px-3 py-1 rounded-md font-medium transition-colors"
                  style={{ background: 'transparent', border: '1px solid #2D3748', color: '#94A3B8' }}
                  data-testid="open-tax-preset-picker"
                >
                  Pick a different region…
                </button>
              </div>
            </div>
          </div>
        ) : (businessCountry || businessState) ? (
          <div
            className="mb-3 p-3 rounded-lg text-xs"
            style={{ background: '#0B1321', border: '1px solid #2D3748', color: '#94A3B8' }}
          >
            We couldn't auto-detect a tax rate for <strong className="text-white">{businessState}, {businessCountry}</strong>.{' '}
            <button onClick={openPresetPicker} className="underline" style={{ color: '#1E88E5' }} data-testid="open-tax-preset-picker-fallback">
              Pick from the list
            </button>{' '}
            or enter values manually below.
          </div>
        ) : (
          <div
            className="mb-3 p-3 rounded-lg text-xs"
            style={{ background: '#0B1321', border: '1px solid #2D3748', color: '#94A3B8' }}
          >
            Set your business state/province in <strong>Settings → Company Information</strong> to get automatic tax suggestions.
          </div>
        )}

        {/* Preset picker — dropdown of all Canadian/US options */}
        {showPresets && presets && (
          <div
            className="mb-3 p-3 rounded-lg"
            style={{ background: '#0B1321', border: '1px solid #2D3748' }}
            data-testid="tax-preset-picker"
          >
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-white">Pick a tax preset</p>
              <button onClick={() => setShowPresets(false)} className="text-xs" style={{ color: '#94A3B8' }}>Close</button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4 max-h-72 overflow-y-auto">
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-1.5 font-bold" style={{ color: '#1E88E5' }}>Canada</p>
                <ul className="space-y-1">
                  {(presets.CA || []).map((p) => (
                    <li key={`ca-${p.region_code}`}>
                      <button
                        onClick={() => applyPreset(p)}
                        className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-white/5 flex items-center justify-between"
                        style={{ color: '#E2E8F0' }}
                        data-testid={`preset-CA-${p.region_code}`}
                      >
                        <span>{p.region_name}</span>
                        <span style={{ color: '#94A3B8' }}>{p.label} {Number(p.rate).toFixed(p.rate % 1 === 0 ? 0 : 3)}%</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="text-[10px] uppercase tracking-wider mb-1.5 font-bold" style={{ color: '#1E88E5' }}>United States</p>
                <ul className="space-y-1">
                  {(presets.US || []).map((p) => (
                    <li key={`us-${p.region_code}`}>
                      <button
                        onClick={() => applyPreset(p)}
                        className="w-full text-left px-2 py-1.5 rounded text-xs hover:bg-white/5 flex items-center justify-between"
                        style={{ color: '#E2E8F0' }}
                        data-testid={`preset-US-${p.region_code}`}
                      >
                        <span>{p.region_name}</span>
                        <span style={{ color: '#94A3B8' }}>{p.label} {Number(p.rate).toFixed(p.rate % 1 === 0 ? 0 : 3)}%</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Tax */}
        <div className="grid sm:grid-cols-3 gap-3 mb-4">
          <div className="sm:col-span-2">
            <label className={labelClass} style={labelStyle}>Tax / VAT Registration # (GST, HST, VAT…)</label>
            <input
              type="text"
              value={form.invoice_tax_id}
              onChange={(e) => setForm({ ...form, invoice_tax_id: e.target.value })}
              placeholder="e.g. 12345 6789 RT0001"
              className={inputClass}
              data-testid="invoice-tax-id"
              maxLength={50}
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Tax label</label>
            <input
              type="text"
              value={form.invoice_tax_label}
              onChange={(e) => setForm({ ...form, invoice_tax_label: e.target.value })}
              placeholder="HST"
              className={inputClass}
              data-testid="invoice-tax-label"
              maxLength={30}
            />
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={labelClass} style={labelStyle}>Tax rate (%) — set 0 for no tax line</label>
            <input
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={form.invoice_tax_rate}
              onChange={(e) => setForm({ ...form, invoice_tax_rate: parseFloat(e.target.value || 0) })}
              className={inputClass}
              data-testid="invoice-tax-rate"
            />
          </div>
          <div className="sm:col-span-2 rounded-lg p-3" style={{ border: '1px solid #2D3748' }} data-testid="invoice-numbering-section">
            <label className={labelClass} style={labelStyle}>Invoice numbering</label>
            <p className="text-[11px] mb-2" style={{ color: '#64748B' }} data-testid="invoice-numbering-blurb">
              <span className="text-white font-semibold">Set once.</span> Every invoice you create continues the sequence from here — each job invoice and Daily Site Invoice takes the next number the first time it is downloaded, emailed, exported or pushed to QuickBooks, and keeps that number forever. To carry on from another accounting program, enter the number your next invoice should get.
            </p>
            <NumberingFields
              value={{ prefix: form.invoice_prefix, format: form.invoice_number_format || '', padding: form.invoice_seq_padding ?? 4, next_number: form.invoice_next_number ?? counter + 1 }}
              onChange={(n) => setForm({ ...form, invoice_prefix: n.prefix, invoice_number_format: n.format || null, invoice_seq_padding: n.padding, invoice_next_number: n.next_number })}
              testPrefix="invoice-numbering"
              hint={`${counter} invoice${counter === 1 ? '' : 's'} numbered so far${lastUsed ? ` · last used ${lastUsed}` : ''}`}
            />
          </div>
        </div>

        {/* Currency + Company-default Material Markup (Phase 2 — Feb 2026) */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={labelClass} style={labelStyle}>Invoice currency</label>
            <select
              value={form.invoice_currency}
              onChange={(e) => setForm({ ...form, invoice_currency: e.target.value })}
              className={inputClass}
              data-testid="invoice-currency-select"
            >
              <option value="USD">USD — US Dollar</option>
              <option value="CAD">CAD — Canadian Dollar</option>
              <option value="EUR">EUR — Euro</option>
              <option value="GBP">GBP — British Pound</option>
              <option value="DKK">DKK — Danish Krone</option>
              <option value="SEK">SEK — Swedish Krona</option>
              <option value="NOK">NOK — Norwegian Krone</option>
              <option value="AUD">AUD — Australian Dollar</option>
              <option value="NZD">NZD — New Zealand Dollar</option>
              <option value="INR">INR — Indian Rupee</option>
              <option value="JPY">JPY — Japanese Yen</option>
              <option value="MXN">MXN — Mexican Peso</option>
            </select>
            <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
              All invoices, PDFs, and dashboard amounts will use this currency.
            </p>
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Default material markup (%)</label>
            <input
              type="number"
              step="0.1"
              min="0"
              max="500"
              value={form.material_markup_pct}
              onChange={(e) => setForm({ ...form, material_markup_pct: parseFloat(e.target.value || 0) })}
              className={inputClass}
              data-testid="invoice-material-markup"
              placeholder="0"
            />
            <p className="text-[11px] mt-1" style={{ color: '#64748B' }}>
              Auto-applied to materials' unit price (Cost × (1 + markup)). Customer / job / line overrides win in that order.
            </p>
          </div>
        </div>

        {/* Reply-to + Payment terms */}
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className={labelClass} style={labelStyle}>Reply-to email (where customer replies land)</label>
            <input
              type="email"
              value={form.invoice_reply_to}
              onChange={(e) => setForm({ ...form, invoice_reply_to: e.target.value })}
              placeholder="billing@yourcompany.com"
              className={inputClass}
              data-testid="invoice-reply-to"
            />
          </div>
          <div>
            <label className={labelClass} style={labelStyle}>Payment terms (single line)</label>
            <input
              type="text"
              value={form.invoice_payment_terms}
              onChange={(e) => setForm({ ...form, invoice_payment_terms: e.target.value })}
              placeholder="Net 30 — e-transfer to billing@yourcompany.com"
              className={inputClass}
              data-testid="invoice-payment-terms"
              maxLength={200}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="mb-4">
          <label className={labelClass} style={labelStyle}>
            Footer text (free-form — payment instructions, thank-you note, etc.)
          </label>
          <textarea
            rows={3}
            value={form.invoice_footer_text}
            onChange={(e) => setForm({ ...form, invoice_footer_text: e.target.value })}
            placeholder="Thank you for your business. Please pay within 30 days."
            className={`${inputClass} resize-none`}
            data-testid="invoice-footer-text"
            maxLength={1000}
          />
        </div>

        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50 flex items-center gap-2"
          style={{ background: '#1E88E5', color: '#fff' }}
          data-testid="invoice-settings-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          {saving ? 'Saving…' : 'Save invoice settings'}
        </button>
      </div>

      {/* Materials Catalogue (Phase 3 — Feb 2026) */}
      <MaterialCatalogManager token={token} />

      {/* How to use */}
      <div
        className="rounded-xl p-4 flex items-start gap-3"
        style={{ background: 'rgba(30,136,229,0.07)', border: '1px solid rgba(30,136,229,0.25)' }}
      >
        <FileText className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: '#1E88E5' }} />
        <div className="text-sm" style={{ color: '#E2E8F0' }}>
          <p className="font-semibold mb-1 text-white">How to generate an invoice</p>
          <p style={{ color: '#94A3B8' }}>
            On any <strong>completed</strong> job in <em>Jobs → Completed</em>, you'll see a
            "Download PDF" and "Email to Customer" button. Both use this template. The customer's
            email comes from the customer record on the job.
          </p>
        </div>
      </div>
    </div>
  );
}
